import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import { State, SkillDto, UserProfileDto } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ReactPlayer from "react-player/lazy";

import { TableSpinner } from "../../components";
import { NoResultFound } from "../../components/Common";
import DeleteModal from "../../components/Common/DeleteModal";
import { AddEditSkillModal } from "./AddEditSkillModal";
import {
  deleteSkill,
  deleteSkillConsent,
  getSkillsByLesson,
  toggleAddEditSkillModal,
  toggleSkillState,
} from "../../store/skill/actions";
import smallImage14 from "../../assets/images/small/img-14.png";
import { RoleEnum } from "../../enums";
import { CopyCutSkillModal } from "./CopyCutSkillModal";
import {
  resetChapterSelectListBySubject,
  resetCourseSelectListLocal,
  resetGradeSelectListByCourseLocal,
  resetLessonSelectListByChapter,
  resetSubjectSelectListByGrade,
} from "../../store/actions";

type TProps = {
  lessonId: string;
  header?: any;
};
export const SkillsPage: React.FC<TProps> = ({ lessonId, header }) => {
  //#region hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [skillsToCopy, setSkillsToCopy] = useState<string[]>([]);
  //#endregion

  //#region internal state
  const [showSkillDeleteModal, setShowSkillDeleteModal] =
    useState<boolean>(false);
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);
  const [showAddEditSkillModal, setShowAddEditSkillModal] =
    useState<boolean>(false);
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null);
  const [showCopyCutModal, setShowCopyCutModal] = useState(false);
  const [action, setAction] = useState<string>("copy");
  //#endregion

  //#region redux state
  const { loading: loadingSkills, response: skills } = useSelector(
    (state: any): State<SkillDto[]> => state.skill.skillsByLesson
  );
  const {
    loading: deleteSkillLoading,
    success: deleteSkillSuccess,
    error: deleteSkillError,
  } = useSelector((state: any): State<SkillDto> => state.skill.deleteSkill);

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  //#endregion

  //#region effects

  useEffect(() => {
    dispatch(getSkillsByLesson(lessonId, history));
  }, [lessonId]);

  useEffect(() => {
    if (!deleteSkillLoading && deleteSkillSuccess) {
      setDeletingSkillId(null);
      setShowSkillDeleteModal(false);
    }
  }, [deleteSkillLoading]);
  //#endregion

  //#region handlers

  const onEdit = (id: string) => {
    setEditingSkillId(id);
    setShowAddEditSkillModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingSkillId(id);
    setShowSkillDeleteModal(true);
    dispatch(deleteSkillConsent(id));
  };

  const onDeleteConfirm = () => {
    dispatch(deleteSkill(deletingSkillId as string, history));
    setDeletingSkillId(null);
  };

  const onAdd = () => {
    dispatch(toggleAddEditSkillModal());
    setShowAddEditSkillModal(true);
  };

  const onToggleState = (id: string) => {
    dispatch(toggleSkillState(id as string, history));
  };

  const onChangeSkillCheckbox = (event: any, id: string) => {
    const { target } = event;
    const { checked } = target;
    if (checked) {
      setSkillsToCopy([...skillsToCopy, id]);
    } else {
      setSkillsToCopy(skillsToCopy.filter((s) => s !== id));
    }
  };
  const onCopySkills = (action: string) => {
    setAction(action);
    setShowCopyCutModal(true);
  };

  const resetCopyCutModal = () => {
    dispatch(resetLessonSelectListByChapter());
    dispatch(resetChapterSelectListBySubject());
    dispatch(resetSubjectSelectListByGrade());
    dispatch(resetGradeSelectListByCourseLocal());
    dispatch(resetCourseSelectListLocal());
  };

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{header}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              {skillsToCopy.length > 0 && (
                <Button color="success" onClick={() => onCopySkills("copy")}>
                  <i className="ri-file-copy-fill me-1 align-bottom"></i>
                  {t("copy")}
                </Button>
              )}
              {skillsToCopy.length > 0 && (
                <Button color="success" onClick={() => onCopySkills("cut")}>
                  <i className="ri-scissors-line me-1 align-bottom"></i>
                  {t("cut")}
                </Button>
              )}
              <Button color="success" onClick={() => onAdd()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addItem", { item: "skill" })}
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          {loadingSkills && <TableSpinner />}
          {!loadingSkills && (!skills || skills.length == 0) && (
            <NoResultFound />
          )}
          {!loadingSkills && skills && skills.length > 0 && (
            <React.Fragment>
              <Col lg={12}>
                <Row className="team-list grid-view-filter">
                  {skills.map((item: SkillDto, key) => (
                    <Col key={key} xs={12} sm={6} lg={4} xl={3} xxl={3}>
                      {" "}
                      <Card className="team-box">
                        {item?.video && item.video.path ? (
                          <div className="player-wrapper">
                            <ReactPlayer
                              className="react-player"
                              url={item.video.path}
                              width="100%"
                              height="100%"
                              controls={true}
                              muted={true}
                            />
                          </div>
                        ) : (
                          <img
                            src={smallImage14}
                            alt=""
                            className="img-fluid"
                          />
                        )}
                        <CardBody>
                          <UncontrolledDropdown
                            style={
                              i18n.language === "ar"
                                ? { position: "absolute", top: 10, left: 15 }
                                : { position: "absolute", top: 10, right: 15 }
                            }
                          >
                            <DropdownToggle
                              tag="a"
                              id="dropdownMenuLink2"
                              role="button"
                            >
                              <i className="ri-more-fill fs-17"></i>
                            </DropdownToggle>

                            <DropdownMenu>
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item edit-list"
                                onClick={() => onEdit(item.id)}
                              >
                                <i className="ri-pencil-line me-2 align-bottom text-muted"></i>
                                {t("edit")}
                              </DropdownItem>
                              {profile &&
                                profile.role.key == RoleEnum.SUPER_ADMIN && (
                                  <DropdownItem
                                    style={{
                                      alignSelf: "flex-start",
                                      display: "flex",
                                    }}
                                    className="dropdown-item edit-list"
                                    onClick={() => onToggleState(item.id)}
                                  >
                                    <i
                                      className={`${
                                        item.isActive
                                          ? "ri-forbid-line"
                                          : "ri-checkbox-circle-line text-danger"
                                      }  me-2 align-bottom text-muted`}
                                    ></i>
                                    {item.isActive
                                      ? t("inactivate")
                                      : t("activate")}
                                  </DropdownItem>
                                )}
                              <DropdownItem
                                style={{
                                  alignSelf: "flex-start",
                                  display: "flex",
                                }}
                                className="dropdown-item remove-list"
                                onClick={() => onDelete(item.id)}
                              >
                                <i className="ri-delete-bin-5-line me-2 align-bottom text-muted"></i>
                                {t("delete")}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                          <div className="form-check mb-2">
                            <Input
                              onChange={(e) =>
                                onChangeSkillCheckbox(e, item.id)
                              }
                              className="form-check-input"
                              type="checkbox"
                              id={`checkbox_${item.id}`}
                              checked={
                                skillsToCopy.find((i) => i == item.id)
                                  ? true
                                  : false
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor={`checkbox_${item.id}`}
                            >
                              {t("checkToCopyOrCut")}
                            </Label>
                          </div>
                          <Row>
                            <Col sm={12} className="mb-2">
                              <Badge
                                color={`${
                                  item.isActive ? "primary" : "danger"
                                }`}
                              >
                                {item.isActive ? t("active") : t("inActive")}
                              </Badge>
                              &nbsp;
                              {item.sequenceNumber &&
                                item.sequenceNumber > 0 && (
                                  <Badge color={`${"secondary"}`}>
                                    {item.sequenceNumber}
                                  </Badge>
                                )}
                              &nbsp;
                              {item.questions &&
                                profile?.role.key === RoleEnum.SUPER_ADMIN && (
                                  <Badge
                                    color={`${
                                      item?.questions.length <= 2
                                        ? "danger"
                                        : "primary"
                                    }`}
                                  >
                                    {t("questions")}({item?.questions.length})
                                  </Badge>
                                )}
                            </Col>
                            <Col sm={12}>
                              <h5>
                                {i18n.language === "ar"
                                  ? item.name.ar
                                  : item.name.en}
                              </h5>
                              <p>
                                {i18n.language === "ar"
                                  ? item.content.ar
                                  : item.content.en}
                              </p>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </React.Fragment>
          )}
        </Row>
        <DeleteModal
          loading={deleteSkillLoading}
          show={showSkillDeleteModal}
          onDeleteClick={() => onDeleteConfirm()}
          onCloseClick={() => {
            setShowSkillDeleteModal(false);
          }}
          error={deleteSkillError}
        />
        <AddEditSkillModal
          lessonId={lessonId}
          id={editingSkillId}
          show={showAddEditSkillModal}
          onCloseClick={() => {
            setShowAddEditSkillModal(false);
            setEditingSkillId(null);
            dispatch(getSkillsByLesson(lessonId, history));
          }}
        />
        <CopyCutSkillModal
          skillsIds={skillsToCopy}
          show={showCopyCutModal}
          action={action}
          onCloseClick={() => {
            setShowCopyCutModal(false);
            resetCopyCutModal();
            setSkillsToCopy([]);
            dispatch(getSkillsByLesson(lessonId, history));
          }}
        />
      </Container>
    </React.Fragment>
  );
};
